<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <!-- <div v-show="host.match(/^localhost|^192/)\" class="debugMessage">
    <p>vimeoHash = {{ vimeoHash }}</p>
    <p>showcase = {{ showcase }}</p>

    <p>description {{ description }}</p>

    <p>signedIn = {{ signedIn }}</p>
    <p>signInRequired = {{ signInRequired }}</p>
    <p>lookup_value = {{ lookup_value }}</p>
    <p>route.path = {{ $route.path }}</p>
  </div> -->
  <figure
    v-if="options && options.showcases"
    :class="{hasAccessStack: needsSignIn || needsRegistration}"
  >
    <!-- a free video, signed out: only Sign in, the locked videos' button at full width (Sign-In
         then says it's free); the thumbnail's badge does the same (2026-10-14) -->
    <div v-if="needsSignIn && isFreeVideo" class="access-stack">
      <div class="access-inline-buttons">
        <VideoAccessActionButton href="#sign-in" label="Sign in" @click="askToSignIn" />
      </div>
    </div>
    <div v-else-if="needsSignIn || needsRegistration" class="access-stack">
      <VideoAccessInfoButton
        label="How do I unlock this video?"
        is-prominent
        @click="router.push({name: 'Videos_Unlock'})" />
      <div class="access-inline-buttons">
        <VideoAccessActionButton
          :href="registrationUrl(videoType)"
          label="Purchase access"
          @click="goToRegister" />
        <VideoAccessActionButton
          v-show="needsSignIn"
          href="#sign-in"
          label="Sign in"
          @click="openLockedSignIn" />
      </div>
    </div>
    <div id="wrapper" ref="mediaWrapper">
      <transition-group name="fade" tag="div">
        <!-- https://stackoverflow.com/q/55006789 -->
        <!-- https://developer.vimeo.com/player/sdk/embed -->
        <vue-vimeo-player
          v-if="lookup_value && !route.hash"
          key="player"
          class="child"
          :video-id="lookup_value"
          :options="playerOptions"
          @ready="onPlayerReady"
          @loaded="onVersionLoaded"
          @ended="onEnded"
          @play="isEnded = false" />
        <div
          v-else-if="options.thumbnails['base_link'] && options.thumbnails['sizes'][3]"
          key="thumbnail"
          class="child thumbnail-container"
          :class="{locked: isAccessBlocked}"
          @click="onThumbnailClick">
          <img :src="getThumbnailUrl(options.thumbnails, options.thumbnails['sizes'][3])" />
          <!-- a free video, signed out: the gallery's badge instead of the lock; a click opens
               Sign-In saying it's free (2026-10-14) -->
          <div v-if="isFreeVideo && needsSignIn" class="lock-overlay">
            <span class="freeTag">Free with sign-up</span>
          </div>
          <div v-else-if="isAccessBlocked" class="lock-overlay">
            <span class="lock-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white">
                <path
                  d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
              </svg>
            </span>
          </div>
        </div>
        <p v-else key="empty">No video or thumbnail available</p>
      </transition-group>
      <SkipControls
        v-if="lookup_value && !route.hash && !isEnded"
        :player="skipPlayer"
        :container="mediaWrapper" />
      <EndScreen v-if="isEnded" :options="options" :items="recommendations" @replay="replay" />
    </div>
    <figcaption>
      <!-- "MARTIN DOHRN: Ballet of the Birds": one heading, the speaker in bold capitals, the talk
           semibold in title case (2026-10-14) -->
      <h4 v-if="title" class="videoTitle">
        <!-- &#32;: the space after the colon (Vue drops whitespace between elements on two
             lines) -->
        <span v-if="titleParts.speaker" class="titleSpeaker">{{ titleParts.speaker }}:&#32;</span>
        <span class="titleTalk">{{ getTitleCase(titleParts.talk) }}</span>
        <Slides :name="fullName" :year="year" />
      </h4>
      <VersionSwitch
        v-if="speakerOnlyHash"
        :is-speaker-only="isSpeakerOnly"
        @update:is-speaker-only="setSpeakerOnly" />
      <VideoDescription
        v-if="description"
        :description="description"
        :video-name="title"
        :year="year" />
    </figcaption>
  </figure>
  <div v-if="goToVideosButton">
    <p class="message">Unknown video identifier '{{ vimeoHash }}'</p>
    <ButtonBespoke @click="$router.push({name: 'Videos'})">Show all Videos</ButtonBespoke>
  </div>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`VideoBespoke: ${msg}`); // eslint-disable-line
const props = defineProps(["options"]);
defineEmits(["filterByYear", "filterBySession"]);
import {computed, inject, onMounted, ref, shallowRef, watch} from "vue";
import {useEventListener} from "@vueuse/core";
import {useRoute, useRouter} from "vue-router";
import {registrationUrl, redirectToRegistration} from "@/composables/useRedirectToRegistration";
import {getPageUrl, getShortDescription, setPageMeta} from "@/composables/usePageMeta";
const isShowcase = computed(() => props.options.session === "Showcase");
import {useClerk} from "@clerk/vue";
import ButtonBespoke from "@/components/ButtonBespoke";
import {vueVimeoPlayer} from "vue-vimeo-player";
import Slides from "@/components/People/Person/Slides.vue";
import VideoDescription from "@/components/Video/Description.vue";
import EndScreen from "@/components/Video/EndScreen.vue";
import VersionSwitch from "@/components/Video/VersionSwitch.vue";
import SkipControls from "@/components/Video/SkipControls.vue";
import {getCanFullscreen} from "@/components/Video/useOverlayFullscreen";
import {
  followVideoUrl,
  getCaptionStart,
  getUrlParam,
  setUrlParams
} from "@/components/Video/videoUrlState";
import {followVideoAnalytics} from "@/components/Video/videoAnalytics";
import {getIsFirstOffer, requestSignIn, setSignInRequest}
  from "@/composables/useSignInRequest";
import {getRecommendations} from "@/components/Video/recommendations";
import useSpeakerIndex from "@/composables/useSpeakerIndex";
import {getTitleCase} from "@/composables/useTitleCase";
import VideoAccessInfoButton from "@/components/Video/VideoAccessInfoButton.vue";
import VideoAccessActionButton from "@/components/Video/VideoAccessActionButton.vue";
import {getFullstackUrl} from "@/composables/useFullstackUrl";
import {getIsPublic, getThumbnailUrl, getVideoStatus} from "@/components/Video/videoCards";
const isClerkEnabled = typeof window !== "undefined" && window.__clerkEnabled === true;
const clerk = isClerkEnabled ? useClerk() : ref(null);
const {isSignedIn, isLoaded} = inject("authentication");

const route = useRoute();
const router = useRouter();
const nameRegex = /^(.*):/;

const goToVideosButton = ref(false);
const needsSignIn = ref(false);
const needsRegistration = ref(false);
const isAccessBlocked = computed(() => needsSignIn.value || needsRegistration.value);
const lookup_value = ref("");
const description = ref("");

// start muted unless the browser will autoplay with sound (a blocked attempt logs a warning);
// Firefox reports its policy, including the user's site setting; others: sound after in-app
// navigation only, where a click allows it
function getIsMutedStart() {
  const policy = navigator.getAutoplayPolicy?.("mediaelement");
  if (policy) return policy !== "allowed";
  return !window.history.state?.back;
}
const isMuted = getIsMutedStart();

const vimeoHash = computed(() => route.params.vimeoHash);
// a free video needs only sign-in (the lookup's isFree): Sign-In opens once as the page loads,
// saying so; closed, the video stays locked with its Sign in button and "Sign up to watch for
// free" badge (2026-10-14). Signed in, the isSignedIn watch below looks it up again and it plays.
const isFreeVideo = ref(false);

function askToSignIn() {
  requestSignIn(router, "video");
}

// once per video per page load: closing the dialog changes the hash, which rebuilds this page,
// so the note is kept outside it (useSignInRequest.js)
function offerSignInOnce() {
  if (route.hash || !getIsFirstOffer(vimeoHash.value)) return;
  askToSignIn();
}
// the end of a video: VIZBI's recommendations over the player (EndScreen.vue), 2026-10-14
const {getSpeaker} = useSpeakerIndex();
let player = null; // the Vimeo player (the SDK's), from vue-vimeo-player's "ready"
const skipPlayer = shallowRef(null); // the same, for the skip buttons (SkipControls.vue)
const mediaWrapper = ref(null);
// where a page element can go fullscreen, ours replaces Vimeo's (useOverlayFullscreen.js)
const hasOwnFullscreen = getCanFullscreen();
// https://developer.vimeo.com/player/sdk/embed; texttrack: ?cc= (videoUrlState.js), left out for
// "off" (the SDK would send "undefined")
function getPlayerOptions() {
  const texttrack = getCaptionStart();
  return {
    responsive: true,
    title: false,
    portrait: false,
    byline: false,
    autoplay: true,
    muted: isMuted,
    fullscreen: !hasOwnFullscreen,
    loop: isShowcase.value,
    ...(texttrack ? {texttrack} : {})
  };
}
const playerOptions = computed(getPlayerOptions);
const isEnded = ref(false);
const recommendations = ref([]);
watch(vimeoHash, () => (isEnded.value = false));

function onPlayerReady(readyPlayer) {
  player = readyPlayer;
  skipPlayer.value = readyPlayer;
  // ?t= and ?end=, and where this browser left it (not a looping showcase's; videoUrlState.js)
  followVideoUrl(readyPlayer, () => (isShowcase.value ? "" : vimeoHash.value));
  // GA's video_start and video_watched (videoAnalytics.js)
  followVideoAnalytics(readyPlayer, () => ({id: vimeoHash.value, record: video.value}));
}

// fullscreen would hide the overlay: leave it first
async function onEnded() {
  if (isShowcase.value) return; // a looping showcase video never ends
  recommendations.value = getRecommendations(vimeoHash.value, props.options.showcases, getSpeaker);
  if (await player?.getFullscreen().catch(() => false)) await player.exitFullscreen();
  isEnded.value = recommendations.value.length > 0;
}

async function replay() {
  isEnded.value = false;
  await player?.setCurrentTime(0);
  player?.play();
}
const showcase = computed(() => props.options.get_showcase(vimeoHash.value));
const video = computed(() => {
  if (!showcase.value) return "";
  return props.options.showcases[showcase.value]["videos"][vimeoHash.value];
});
const title = computed(() => video.value?.name);
// a talk with two versions (VIZBI 2016, decision S1): the speaker-only one's hash; the choice is
// kept for the next such talk (this browser only), and in the URL (?version=), which comes first
const VERSION_KEY = "videoVersion";
function getIsSpeakerOnlyStart() {
  const version = getUrlParam("version");
  if (version) return version === "speaker";
  try {
    return localStorage.getItem(VERSION_KEY) === "speaker";
  } catch {
    return false;
  }
}
const speakerOnlyHash = computed(() => video.value?.speakerOnly || "");
const isSpeakerOnly = ref(getIsSpeakerOnlyStart());
const lookupHash = computed(() => {
  return (isSpeakerOnly.value && speakerOnlyHash.value) || vimeoHash.value;
});
let resumeAt = 0; // the second to carry on from in the other version
// "A & B: Title: preview" → {speaker: "A & B", talk: "Title: preview"}; no speaker without ": "
const titleParts = computed(() => {
  const index = String(title.value || "").indexOf(": ");
  if (index === -1) return {speaker: "", talk: title.value};
  return {speaker: title.value.slice(0, index), talk: title.value.slice(index + 2)};
});
const year = computed(() => showcase.value.match(/\d+/)[0]);
const videoType = computed(() =>
  /masterclass/i.test(showcase.value || "") ? "masterclass" : "workshop"
);
const fullName = computed(() => {
  let nameMatch = title.value.match(nameRegex);
  if (!nameMatch) return;
  nameMatch = nameMatch[1];
  log(`nameMatch = ${nameMatch}`);
  return nameMatch;
});

async function goToRegister() {
  try {
    await clerk.value?.session?.touch();
  } catch {
    // touch may fail on slow connections; proceed regardless
  }
  redirectToRegistration(videoType.value);
}

// a locked video's Sign in or lock: the dialog names its event (useSignInRequest.js)
const getLockedReason = () => {
  return videoType.value === "masterclass" ? "lockedMasterclass" : "lockedWorkshop";
};
// signed in from the lock (Seán, 2026-10-23): the account's access looked up again; it plays if
// it covers this video, else #register for its event (go_back_to this video)
async function continueAfterLockedSignIn() {
  await clerk.value?.user?.reload();
  await get_meta_data();
  if (needsRegistration.value) goToRegister();
}
function openLockedSignIn() {
  requestSignIn(router, getLockedReason(), continueAfterLockedSignIn);
}

function onThumbnailClick() {
  if (!isSignedIn.value && isFreeVideo.value) return askToSignIn();
  if (!isSignedIn.value) return openLockedSignIn();
  if (needsRegistration.value) goToRegister();
}

function invalidHash() {
  console.warn("Probably caused by invalid vimeoHash in URL");
  goToVideosButton.value = true;
}

async function get_lookup_value(vimeoHash) {
  const base = getFullstackUrl();
  if (!base) {
    console.error("VUE_APP_FULLSTACK is not set");
    return null;
  }
  const url = `${base}/api/v1/videos/lookup/${vimeoHash}`;
  let token = null;
  try {
    token = clerk.value?.session ? await clerk.value.session.getToken() : null;
  } catch (error) {
    console.error("Failed to get auth token:", error);
  }
  try {
    const response = await fetch(url, {
      headers: token ? {Authorization: `Bearer ${token}`} : {}
    });
    if (!response.ok) {
      console.error(`Lookup failed: status=${response.status}`);
      return null;
    }
    return response.json();
  } catch (error) {
    console.error(`Problem accessing fullstack: ${url}`, error);
  }
}

async function get_meta_data(retryCount = 0) {
  if (!vimeoHash.value) return;
  goToVideosButton.value = false;
  const data = await get_lookup_value(lookupHash.value);
  if (!data) return console.error("No response from backend.");
  if (data.ok) {
    needsSignIn.value = false;
    needsRegistration.value = false;
    lookup_value.value = data.videoId;
    return;
  }
  if (data.action === "login") {
    if (isSignedIn.value && retryCount < 3) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      return get_meta_data(retryCount + 1);
    }
    needsSignIn.value = true;
    needsRegistration.value = false;
    isFreeVideo.value = Boolean(data.isFree);
    if (data.isFree) offerSignInOnce();
    return;
  }
  if (data.action === "register") {
    needsSignIn.value = false;
    needsRegistration.value = true;
    return;
  }
  invalidHash();
}

// signed out, the video's own record already gives the lookup's answer (its rules: a free
// Showcase video plays for anyone, other free videos need sign-in, the rest sign-in or purchase),
// so the access buttons show at once; the lookup then confirms it. They waited for it: 20–30 s on
// a phone after a gallery click, the request queued behind the gallery's images (2026-10-18)
function showSignedOutAccess() {
  const record = video.value;
  if (!record || getIsPublic(record)) return;
  needsSignIn.value = true;
  isFreeVideo.value = getVideoStatus(record) === "free";
  // reloaded with Sign-In open: the same lines as from the lock
  if (route.hash !== "#sign-in" || isFreeVideo.value) return;
  setSignInRequest(getLockedReason(), continueAfterLockedSignIn);
}

// Clerk's own cookie says at once whether this browser is signed in ("0" or none: signed out),
// before Clerk has loaded (~1 s); signed out, the access buttons then show with the thumbnail,
// instead of pushing it down when Clerk loads (2026-10-21, Seán)
function getIsSignedOutHint() {
  const value = document.cookie.match(/(?:^|; )__client_uat=([^;]*)/)?.[1];
  return !value || value === "0";
}

watch(
  video,
  (record) => {
    if (!record || isLoaded.value || !getIsSignedOutHint()) return;
    showSignedOutAccess();
  },
  {immediate: true}
);

watch(
  isLoaded,
  async (loaded) => {
    if (!loaded || isSignedIn.value || lookup_value.value) return;
    showSignedOutAccess();
    await get_meta_data();
  },
  {immediate: true}
);

watch(
  isSignedIn,
  async (signedIn) => {
    if (!signedIn || lookup_value.value) return;
    if (window.location.hash === "#sign-in") router.replace({hash: ""});
    await clerk.value?.user?.reload();
    await get_meta_data();
  },
  {immediate: true}
);

// access bought while on a locked page (2026-10-21, Seán: decision V6): asked again when the
// page comes back into view (the tab, the window, the return from paying); the server reads the
// account each time, so it then plays without a reload
function onVisible() {
  if (document.visibilityState !== "visible" || !needsRegistration.value) return;
  get_meta_data();
}
useEventListener(document, "visibilitychange", onVisible);
useEventListener(window, "focus", onVisible);

// the other version: same access, so the lookup gives its id; the player loads it (its "loaded"
// then seeks to where this one was)
async function setSpeakerOnly(value) {
  if (value === isSpeakerOnly.value) return;
  resumeAt = (await player?.getCurrentTime().catch(() => 0)) || 0;
  isSpeakerOnly.value = value;
  setUrlParams({version: value ? "speaker" : "slides"});
  try {
    localStorage.setItem(VERSION_KEY, value ? "speaker" : "slides");
  } catch {
    // storage blocked (a private window): the choice lasts for this page only
  }
  await get_meta_data();
}

async function onVersionLoaded() {
  if (!resumeAt) return;
  await player?.setCurrentTime(resumeAt).catch(() => {});
  resumeAt = 0;
  player?.play().catch(() => {});
}

// the showcases can arrive after the first lookup: a remembered Speaker only then looks again;
// the URL names the version shown, so a link opens the same one
watch(speakerOnlyHash, (hash) => {
  if (!hash) return;
  setUrlParams({version: isSpeakerOnly.value ? "speaker" : "slides"});
  if (isSpeakerOnly.value && lookup_value.value) get_meta_data();
}, {immediate: true});

onMounted(() => {
  log("Video mounted()");
});
watch(video, (v) => {
  description.value = v?.description ?? "";
}, {immediate: true});

function getVideoData(videoInfo, path) {
  return {
    "@type": "VideoObject",
    name: videoInfo.name,
    description: getShortDescription(videoInfo.description) || videoInfo.name,
    thumbnailUrl: videoInfo.thumbnailImageUrl,
    duration: videoInfo.duration ? `PT${videoInfo.duration}S` : undefined,
    url: getPageUrl(path)
  };
}

watch(video, (videoInfo) => {
  if (!videoInfo?.name) return;
  setPageMeta(route.path, {
    title: `${videoInfo.name} - VIZBI ${year.value}`,
    description: getShortDescription(videoInfo.description),
    image: videoInfo.thumbnailImageUrl,
    type: "video.other",
    structuredData: [getVideoData(videoInfo, route.path)]
  });
}, {immediate: true});
</script>

<style scoped>
.embargo {
  color: #929292;
  font-style: italic;
  font-weight: 300;
}

figure {
  display: flex;
  flex-direction: column;
  margin: 20px 0 0 0;
  width: 100% !important;
}
/* with the unlock bar on top, no top margin: the bar sits where "How do I unlock all videos?"
   does on /Videos, so it doesn't shift between the two pages */
figure.hasAccessStack {
  margin-top: 0;
}
.access-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  width: 100%;
  box-sizing: border-box;
}
.access-inline-buttons {
  display: flex;
  gap: 8px;
}
.access-inline-buttons > * {
  flex: 1;
}
.overlay-buttons {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  right: 0.5rem;
  display: flex;
  justify-content: space-between;
  z-index: 10;
  pointer-events: none;
}
.overlay-buttons > * {
  pointer-events: auto;
}
.overlay-buttons--right {
  justify-content: flex-end;
}
#wrapper {
  position: relative;
  /* min-height: 360px; based on thumbnail chosen */
  padding-bottom: calc(100% / (640 / 360)); /* Replace "16 / 9" with your desired aspect ratio */
}
/* our fullscreen (useOverlayFullscreen.js): the player fills the screen, Vimeo letterboxes */
#wrapper:fullscreen {
  padding-bottom: 0;
  background: #000;
}
/* the same in Safari before 16.4 (a separate rule: a browser drops a rule with a selector it
   doesn't know) */
#wrapper:-webkit-full-screen {
  padding-bottom: 0;
  background: #000;
}
/* Vimeo's responsive box keeps the 16:9 height at the top: in fullscreen it fills the screen,
   and Vimeo letterboxes the video in the middle */
#wrapper:fullscreen :deep(.child > div) {
  height: 100%;
  padding: 0 !important;
}
#wrapper:-webkit-full-screen :deep(.child > div) {
  height: 100%;
  padding: 0 !important;
}
#wrapper .child {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* Adjust the object-fit property as needed */
}
.thumbnail-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.thumbnail-container.locked {
  cursor: pointer;
}
.lock-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
/* the gallery's free-video badge (GalleryItem.vue), larger: the page's one way in */
.freeTag {
  padding: 0.5rem 1.1rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--orange-on-dark); /* the access buttons' orange (VideoAccessActionButton.vue) */
  background: rgba(0, 0, 0, 0.72);
  border-radius: 999px;
}
.lock-icon {
  width: 64px;
  height: 64px;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lock-icon svg {
  width: 32px;
  height: 32px;
}
figcaption {
  /* flex: 1; */
}
.description {
  white-space: pre-wrap;
}

/* the video's heading (2026-10-14): larger (26px, was the h4's 1.2rem), the strongest text
   colour, white in dark mode */
figcaption .videoTitle {
  font-size: 1.625rem;
  line-height: 1.25;
}
.darkMode figcaption .videoTitle {
  color: #fff;
}
.titleSpeaker {
  font-weight: 700;
  text-transform: uppercase;
}
.titleTalk {
  font-weight: 600;
}
.narrow figcaption .videoTitle {
  font-size: clamp(1.1rem, 5.4dvw, 1.625rem);
}

/* a phone's: scoped (was :global(.narrow), which compiled to .narrow and sized every page's
   text; that size is now global.css's own, 2026-10-22) */
.narrow figcaption .description {
  font-size: clamp(0.75rem, 3.33dvw, 1rem);
}
#thumbnail-large {
  cursor: default;
}

/* the one leaving (e.g. "No video or thumbnail available" before the record arrives) fades out
   over the new one, not below it: it kept its space for the 0.9 s, then the thumbnail resized */
.fade-leave-active {
  position: absolute;
  top: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.9s ease;
  overflow: hidden;
  visibility: visible;
  width: 100%;
  opacity: 1;
}

.fade-enter,
.fade-leave-to {
  visibility: hidden;
  width: 100%;
  opacity: 0;
}

.child {
  width: 100%;
}
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}
.v-leave,
.v-enter-to {
  opacity: 1;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}

.debugMessage {
  color: red;
}
</style>
